import { useEffect, useMemo, useState } from 'react';
import { Upload, History, ShieldCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Input } from '../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Card } from '../../ui/card';

import type { ShortcutAgentMode, ShortcutMode, ShortcutRunDialogSubmission } from './types';

type ShortcutRunDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectionSummary: string;
  sourceFileName: string;
  initialMode?: ShortcutMode;
  onVerifyConnection: () => Promise<void> | void;
  onOpenHistory: () => void;
  onSubmit: (submission: ShortcutRunDialogSubmission) => Promise<void> | void;
  isSubmitting?: boolean;
  connectionVerified?: boolean;
};

const EXAMPLE_PROMPTS = [
  'Audit formulas in the selected range and explain any issues.',
  'Normalize this table and add a chart-ready summary.',
  'Build a variance analysis and highlight missing data.',
];

export default function ShortcutRunDialog({
  open,
  onOpenChange,
  selectionSummary,
  sourceFileName,
  initialMode = 'current-sheet',
  onVerifyConnection,
  onOpenHistory,
  onSubmit,
  isSubmitting = false,
  connectionVerified = false,
}: ShortcutRunDialogProps) {

  const [mode, setMode] = useState<ShortcutMode>('current-sheet');
  const [prompt, setPrompt] = useState('');
  const [agentMode, setAgentMode] = useState<ShortcutAgentMode>('action');
  const [timeoutMinutes, setTimeoutMinutes] = useState('10');
  const [skillsText, setSkillsText] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setPrompt('');
    setMode(initialMode);
    setAgentMode('action');
    setTimeoutMinutes('10');
    setSkillsText('');
    setFiles([]);
  }, [initialMode, open]);

  const skills = useMemo(
    () => skillsText.split(',').map((value) => value.trim()).filter(Boolean),
    [skillsText]
  );

  const timeoutMs = useMemo(() => {
    const minutes = Number(timeoutMinutes);
    if (!Number.isFinite(minutes) || minutes <= 0) {
      return undefined;
    }
    return Math.min(30, minutes) * 60 * 1000;
  }, [timeoutMinutes]);

  const submitDisabled = isSubmitting || !prompt.trim() || (mode === 'upload-files' && files.length === 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl border-slate-700 bg-slate-950 text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Upload className="h-5 w-5 text-emerald-400" />
            Run Shortcut
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Send the current MapleSheet selection or uploaded workbook files to Shortcut, then review the result before applying anything.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 lg:grid-cols-[1.3fr_0.9fr]">
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label>Mode</Label>
              <Select value={mode} onValueChange={(value) => setMode(value as ShortcutMode)}>
                <SelectTrigger className="border-slate-700 bg-slate-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current-sheet">Use current sheet</SelectItem>
                  <SelectItem value="upload-files">Upload file(s)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Instruction</Label>
              <Textarea
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder="Describe what Shortcut should do with this spreadsheet..."
                className="min-h-36 border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500"
              />
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_PROMPTS.map((example) => (
                  <button
                    key={example}
                    type="button"
                    onClick={() => setPrompt(example)}
                    className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300 transition hover:border-emerald-500 hover:text-emerald-300"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>

            {mode === 'upload-files' ? (
              <div className="grid gap-2">
                <Label>Files</Label>
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
                  multiple
                  onChange={(event) => setFiles(Array.from(event.target.files || []).slice(0, 5) as File[])}

                  className="border-slate-700 bg-slate-900 file:border-0 file:bg-transparent file:text-slate-100"
                />
                <p className="text-xs text-slate-500">Shortcut accepts up to 5 files. The first file becomes the main workbook; the rest are context files.</p>
                {files.length > 0 && (
                  <div className="rounded-lg border border-slate-800 bg-slate-900/70 p-3 text-sm text-slate-300">
                    <ul className="space-y-1">
                      {files.map((file) => (
                        <li key={`${file.name}-${file.size}`}>{file.name} · {Math.max(1, Math.round(file.size / 1024))} KB</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="space-y-4">
            <Card className="border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-300">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-medium text-slate-100">Current context</span>
                {connectionVerified ? <Badge className="bg-emerald-600/20 text-emerald-300">Verified</Badge> : null}
              </div>
              <dl className="space-y-2">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-500">Source</dt>
                  <dd>{sourceFileName}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-500">Selection</dt>
                  <dd>{selectionSummary}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-slate-500">Behavior</dt>
                  <dd>{mode === 'current-sheet' ? 'Shortcut will receive an XLSX export generated from the current MapleSheet selection.' : 'Shortcut will receive the uploaded spreadsheet files.'}</dd>
                </div>

              </dl>
            </Card>

            <Card className="space-y-3 border-slate-800 bg-slate-900/70 p-4">
              <div className="grid gap-2">
                <Label>Agent mode</Label>
                <Select value={agentMode} onValueChange={(value) => setAgentMode(value as ShortcutAgentMode)}>
                  <SelectTrigger className="border-slate-700 bg-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="action">action</SelectItem>
                    <SelectItem value="ask">ask</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Timeout (minutes)</Label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={timeoutMinutes}
                  onChange={(event) => setTimeoutMinutes(event.target.value)}
                  className="border-slate-700 bg-slate-900"
                />
              </div>

              <div className="grid gap-2">
                <Label>Skills (comma separated, optional)</Label>
                <Input
                  value={skillsText}
                  onChange={(event) => setSkillsText(event.target.value)}
                  placeholder="audit, cleanup"
                  className="border-slate-700 bg-slate-900"
                />
              </div>
            </Card>
          </div>
        </div>

        <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800" onClick={() => onVerifyConnection()}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              Verify connection
            </Button>
            <Button type="button" variant="outline" className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800" onClick={onOpenHistory}>
              <History className="mr-2 h-4 w-4" />
              History
            </Button>
          </div>

          <Button
            type="button"
            disabled={submitDisabled}
            onClick={() => onSubmit({
              mode,
              prompt: prompt.trim(),
              agentMode,
              timeoutMs,
              skills,
              files,
            })}
            className="bg-emerald-600 text-white hover:bg-emerald-500"
          >
            {isSubmitting ? 'Submitting…' : 'Run Shortcut'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
